/* Tajweed color-coding pipeline.
   ═══════════════════════════════════════════════════════════════════════
   PIPELINE (matches the stages this file is responsible for):

     api.quran.com `text_uthmani_tajweed` (per-verse HTML, fetched +
     cached by backend/quran_repo.py)
       → parseTajweedRuns()      strict-regex tag parser (never markup-trusts)
       → RULE_TO_CATEGORY        source tag  →  Tarteel visual category
       → parseAllRuns()          + Tafkhim inference over the untagged text
       → TAJWEED_CATEGORIES      category    →  {label, description, color}
       → splitTajweedByWord()    re-aligns runs to the page's word grid
       → MadaniPage.jsx          renders each run as a colored <span>
       → MadaniPage.jsx legend   reads the SAME TAJWEED_CATEGORIES object

   One source of truth: TAJWEED_CATEGORIES. Nothing downstream (the parser,
   the renderer, or the legend) hardcodes a hex value of its own — they all
   either look it up here or receive it already resolved from here.

   Source data shape: `text_uthmani_tajweed` is raw HTML — Uthmani Arabic
   text with two kinds of tag wrapping it:
     <tajweed class=rule_name>...</tajweed>  — the letters one tajweed rule
                                                applies to (unquoted attribute
                                                value — verified against the
                                                live API, e.g. `class=ham_wasl`,
                                                not `class="ham_wasl"`)
     <span class=end>٣</span>                — the ayah-end digit (already
                                                Arabic-Indic numerals)
   It's verse-level text, not tokenized per word the way the Madani page's
   `code_v1` glyph data is. `splitTajweedByWord` re-splits it on spaces so
   MadaniPage can colorize each word in place within the *same* per-line
   grid the glyph rendering uses (rather than reflowing the page into a
   separate flowing paragraph — see the long comment in MadaniPage.jsx for
   why that specifically broke ayah ordering). Nothing here trusts the
   upstream HTML as markup: it's only ever read character-by-character
   through a strict regex, never inserted via dangerouslySetInnerHTML.
   ═══════════════════════════════════════════════════════════════════════ */

// ───────────────────────── 1. Categories (source of truth) ─────────────────
// Transcribed directly from Tarteel's own in-app legend text (user-provided
// screenshots of the Tarteel app's "tajweed colors" explainer, Sept 2026).
// Tarteel groups by *acoustic effect*, not one-color-per-rule-name — see
// RULE_TO_CATEGORY below for exactly which source tags fold into which
// category, and why. Hex values are read off the legend's color swatches
// (Tarteel doesn't publish exact hex codes), so treat these as a close
// visual match, not a byte-identical pixel copy.
export const TAJWEED_CATEGORIES = {
  necessaryMadd: {
    label: "Madd: 6",
    description: "Necessary Prolongation",
    color: "#B91C1C", // dark/deep red
  },
  obligatoryMadd: {
    label: "Madd: 4 or 5",
    description: "Obligatory Prolongation",
    color: "#E91E63", // bright pink / magenta
  },
  permissibleMadd: {
    label: "Madd: 2, 4, or 6",
    description: "Permissible Prolongation",
    color: "#F97316", // strong orange
  },
  normalMadd: {
    label: "Madd: 2",
    description: "Normal Prolongation",
    color: "#CA8A04", // golden yellow / ochre
  },
  ghunnah: {
    label: "Ghunnah",
    description: "Nasalization",
    color: "#16A34A", // emerald green
  },
  qalqalah: {
    label: "Qalqala",
    description: "Echoing Sound",
    color: "#22D3EE", // bright light blue / cyan
  },
  tafkhim: {
    label: "Tafkhim",
    description: "Emphatic Pronunciation of Heavy Letters",
    color: "#1D4ED8", // darker, distinctly different blue from Qalqala's cyan
    // Not present as a tag anywhere in our data source — see the "TAFKHIM"
    // section below. Colored letters in this category come from this
    // file's own rule inference, not from an upstream annotation.
    inferred: true,
  },
  silent: {
    label: "Silent",
    description: "Unannounced Pronunciation",
    color: "#9CA3AF", // neutral grey — visibly muted/inactive
  },
};

// ───────────────────────── 2. Source tag → category ─────────────────────
// Every key here was verified against LIVE api.quran.com/api/v4 responses
// (curled directly, Sept 2026 — not assumed from documentation), e.g.:
//   curl https://api.quran.com/api/v4/verses/by_key/2:191?fields=text_uthmani_tajweed
//   curl https://api.quran.com/api/v4/verses/by_key/2:49?fields=text_uthmani_tajweed
//   curl https://api.quran.com/api/v4/verses/by_key/2:6?fields=text_uthmani_tajweed
// gave (among others): ham_wasl, qalaqah, idgham_shafawi, madda_normal,
// ikhafa, ikhafa_shafawi, madda_obligatory, idgham_ghunnah,
// idgham_wo_ghunnah, madda_permissible, ghunnah, slnt, iqlab,
// madda_necessary, laam_shamsiyah — exactly the tags mapped below, no more,
// no fewer. (Note the source's own idiosyncratic spelling "qalaqah", not
// "qalqalah" — the tag name and the category name are deliberately allowed
// to differ.)
//
// GHUNNAH GROUPING (Tarteel screenshot section E): Ikhfa, Ikhfa Shafawi,
// Iqlab, Idgham with Ghunnah, and Idgham Shafawi all produce a nasal sound,
// so Tarteel colors all of them the same green as Ghunnah itself — this is
// NOT "we couldn't tell them apart," it's Tarteel's actual documented
// grouping. Do not re-split these into separate colors.
//
// SILENT GROUPING (Tarteel screenshot section H): Idgham without Ghunnah is
// a letter merging/assimilating with no distinct sound of its own — the
// same "merged, no separate sound" semantics the legend gives for Laam
// Shamsiyyah and Hamzat Wasl — so it's grouped with Silent, not given its
// own color (Tarteel's legend has no separate "Idgham without Ghunnah"
// swatch at all).
export const RULE_TO_CATEGORY = {
  // Madd family — each length gets its own category, no grouping.
  madda_necessary: "necessaryMadd",
  madda_obligatory: "obligatoryMadd",
  madda_permissible: "permissibleMadd",
  madda_normal: "normalMadd",
  // Ghunnah family — every nasal-sound-producing rule shares one color.
  ghunnah: "ghunnah",
  ikhafa: "ghunnah",
  ikhafa_shafawi: "ghunnah",
  iqlab: "ghunnah",
  idgham_ghunnah: "ghunnah",
  idgham_shafawi: "ghunnah",
  // Qalqalah — its own category (source tag is spelled "qalaqah").
  qalaqah: "qalqalah",
  // Silent / assimilated family.
  idgham_wo_ghunnah: "silent",
  ham_wasl: "silent",
  laam_shamsiyah: "silent",
  silent: "silent",
  slnt: "silent",
  // No entry for "tafkhim" — no such source tag exists (see below).
};

const RULE_COLOR = Object.fromEntries(
  Object.entries(RULE_TO_CATEGORY).map(([tag, cat]) => [tag, TAJWEED_CATEGORIES[cat].color])
);

// Attribute value may or may not be quoted across API responses — accept both.
const TAG_RE = /<(tajweed|span) class=(?:"([a-zA-Z_]+)"|([a-zA-Z_]+))>([\s\S]*?)<\/\1>/g;

// Sentinel used in place of a CSS color for the ayah-end digit's run, so the
// caller can render it as a circular badge instead of colored text.
export const END_RUN = "__end__";

/** Parse text_tajweed into a flat, order-preserving list of runs —
 *  `{ text, color }` where `color` is a CSS color string for a recognized
 *  tajweed rule, `END_RUN` for the ayah-end digit, or `null` for plain text
 *  (including the spaces between words, and any rule tag we don't recognize
 *  — an unmapped tag degrades to plain text rather than throwing). Everything
 *  not matched by the tag regex is treated as literal text, never as markup. */
function parseTajweedRuns(raw) {
  const runs = [];
  if (!raw) return runs;
  let last = 0;
  let m;
  TAG_RE.lastIndex = 0;
  while ((m = TAG_RE.exec(raw))) {
    if (m.index > last) runs.push({ text: raw.slice(last, m.index), color: null });
    const rule = m[2] || m[3];
    runs.push({ text: m[4], color: rule === "end" ? END_RUN : (RULE_COLOR[rule] || null) });
    last = TAG_RE.lastIndex;
  }
  if (last < raw.length) runs.push({ text: raw.slice(last), color: null });
  return runs;
}

// ───────────────────────── 3. Tafkhim (heavy-letter) inference ──────────
// TAFKHIM DOES NOT EXIST AS A TAG IN OUR DATA SOURCE. Verified directly:
//   curl https://api.quran.com/api/v4/verses/by_key/2:191?fields=text_uthmani_tajweed
// contains the word "الْحَرَامِ" — Tarteel's own screenshot cites this exact
// word as a Tafkhim (heavy Raa) example — and the API's text_uthmani_tajweed
// leaves it completely untagged. This isn't a gap in one verse: Tafkhim is a
// per-letter *pronunciation* quality applied every time certain letters
// occur, not a discrete rule-instance the way Idgham/Ikhfa/Madd spans are,
// so quran.com's tajweed markup simply never annotates it.
//
// Rather than silently omitting the category, it's inferred here from the
// plain Uthmani text using the two rules that are actually deterministic
// from written diacritics (never "if letter === ر, always heavy"):
//
//  1. The seven Huroof al-Isti'laa (خ ص ض غ ط ق ظ) are unconditionally
//     heavy — a fixed letter-class property, not contextual — so every
//     occurrence is colored with no ambiguity.
//  2. ر (raa') depends on its own vowel, or — when sakin — on the
//     *preceding* letter's vowel, with the standard exception:
//       - raa' carries fatha / damma / tanween-fatha / tanween-damma /
//         superscript alif  → HEAVY
//       - raa' carries kasra / tanween-kasra                    → LIGHT
//       - raa' is sakin (no vowel of its own):
//           - preceding letter had fatha/damma  → HEAVY
//           - preceding letter had kasra         → LIGHT, UNLESS the very
//             next letter after the sakin raa' is itself an Isti'laa
//             letter — the classical exception — in which case it's HEAVY
//             (verified against a real example: 2:49's "فِرْعَوْنَ" — kasra,
//             sakin raa', next letter ع which is NOT Isti'laa → correctly
//             resolves LIGHT, no color)
//           - preceding vowel not determinable from the text → left
//             UNCOLORED, never guessed
//
// Deliberately NOT modeled: waqf (stop-position) re-vocalization — nothing
// in this app tracks "the reader is stopping here," so the Mushaf's written
// (continuous-recitation) vowels are treated as authoritative throughout.
// This covers the classical majority-case rule, not every scholarly
// wajhayn (two-permissible-ways) disagreement.
//
// PRECEDENCE: Tafkhim inference runs ONLY over text NOT already covered by
// a source tajweed tag. An explicit API annotation always wins — e.g. in
// 2:49's "مِّن رَّبِّكُمْ", the raa' of "رَّبِّكُمْ" sits inside the API's own
// idgham_wo_ghunnah span (Noon Sakinah assimilating into it) and keeps that
// tag's grey; it is never repainted dark blue, because we do not override
// a reliable existing annotation with our own inference (see parseAllRuns).
const FATHA = "َ", DAMMA = "ُ", KASRA = "ِ", SUKUN = "ْ", SHADDA = "ّ";
const FATHATAN = "ً", DAMMATAN = "ٌ", KASRATAN = "ٍ", SUPERSCRIPT_ALIF = "ٰ";
const ARABIC_DIACRITIC_RE = /[ً-ٰٟ]/;
const HEAVY_VOWELS = new Set([FATHA, DAMMA, FATHATAN, DAMMATAN, SUPERSCRIPT_ALIF]);
const LIGHT_VOWELS = new Set([KASRA, KASRATAN]);
const ISTILAA_LETTERS = new Set(["خ", "ص", "ض", "غ", "ط", "ق", "ظ"]);
const RAA = "ر";

/** true = heavy (tafkhim), false = light (tarqiq, uncolored), null =
 *  indeterminate from the text (uncolored — never guessed). `text` is the
 *  plain (untagged) run string; `i` is the raa's index within it. */
function raaIsHeavy(text, i) {
  // Own vowel, scanning forward past the raa' (shadda may appear before OR
  // after the vowel mark depending on Unicode canonical combining-class
  // ordering — verified directly, e.g. "رَّحْمَـٰنِ" encodes as
  // ر + FATHA(U+064E) + SHADDA(U+0651) — so both marks are scanned for,
  // not assumed to be in one fixed order).
  let j = i + 1;
  let ownVowel = null;
  while (j < text.length && ARABIC_DIACRITIC_RE.test(text[j])) {
    const c = text[j];
    if (HEAVY_VOWELS.has(c) || LIGHT_VOWELS.has(c)) ownVowel = c;
    j++;
  }
  if (ownVowel) return HEAVY_VOWELS.has(ownVowel);

  // Sakin (or unmarked) raa' — fall back to the preceding letter's vowel.
  let k = i - 1;
  if (text[k] === SHADDA) k--; // skip a doubling mark to reach the real vowel
  const prev = text[k];
  if (LIGHT_VOWELS.has(prev)) {
    let m = i + 1;
    while (m < text.length && ARABIC_DIACRITIC_RE.test(text[m])) m++;
    return ISTILAA_LETTERS.has(text[m]); // the kasra-before-isti'laa exception
  }
  if (HEAVY_VOWELS.has(prev)) return true;
  return null; // can't determine — leave uncolored rather than guess
}

/** Re-scan the plain (color: null) runs from parseTajweedRuns for Tafkhim,
 *  splitting each into smaller runs so a heavy letter (plus its own
 *  trailing diacritics, kept together so a colored letter and its harakat/
 *  shaddah/sukun never separate) gets TAJWEED_CATEGORIES.tafkhim.color.
 *  Runs that already carry a source-tag color (or END_RUN) pass through
 *  untouched — source annotations are never overridden. */
function applyTafkhim(baseRuns) {
  const out = [];
  const tafkhimColor = TAJWEED_CATEGORIES.tafkhim.color;
  baseRuns.forEach((run) => {
    if (run.color !== null) { out.push(run); return; }
    const text = run.text;
    let buf = "";
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      let heavy = false;
      if (ISTILAA_LETTERS.has(ch)) heavy = true;
      else if (ch === RAA) heavy = raaIsHeavy(text, i) === true;
      if (heavy) {
        if (buf) { out.push({ text: buf, color: null }); buf = ""; }
        let j = i + 1;
        while (j < text.length && ARABIC_DIACRITIC_RE.test(text[j])) j++;
        out.push({ text: text.slice(i, j), color: tafkhimColor });
        i = j - 1;
      } else {
        buf += ch;
      }
    }
    if (buf) out.push({ text: buf, color: null });
  });
  return out;
}

function parseAllRuns(raw) {
  return applyTafkhim(parseTajweedRuns(raw));
}

// Rub el-Hizb (۞) and Sajdah (۩) markers occasionally prefix a verse's
// first word. Verified directly: 2:26's own `v.words[0].text` is literally
// "۞ إِنَّ" — the API's word-by-word tokenization bundles the marker AND a
// real space into ONE word entry, not two. If we split purely on spaces
// this one specific space would wrongly produce an extra "word" consisting
// of nothing but the marker, throwing off every word position after it.
const MARK_ONLY_RE = /^[۞۩]+$/;

/** Group fullText[start,end) into contiguous same-color sub-runs, using the
 *  parallel colorAt array built by splitTajweedByWord. */
function groupRun(fullText, colorAt, start, end) {
  const chunk = [];
  let curColor = colorAt[start];
  let curText = fullText[start];
  for (let j = start + 1; j < end; j++) {
    if (colorAt[j] === curColor) {
      curText += fullText[j];
    } else {
      chunk.push({ text: curText, color: curColor });
      curColor = colorAt[j];
      curText = fullText[j];
    }
  }
  chunk.push({ text: curText, color: curColor });
  return chunk;
}

/** Split a verse's text_tajweed into per-word run-chunks, aligned 1:1 with
 *  that verse's `words` array from the Madani page data (word_uthmani
 *  tokens, space-separated, including the trailing ayah-end "word").
 *
 *  IMPORTANT — a <tajweed> tag CAN span a word boundary; this is not an
 *  edge case, it's how idgham/ikhfa/iqlab are annotated whenever the rule
 *  is the last letter of one word merging into the first letter of the
 *  next, which is extremely common. E.g. 2:5's
 *  `<tajweed class=idgham_ghunnah>دًى م</tajweed>` spans "هُدًى" into
 *  "مِّن" — verified directly against the live API. An earlier version of
 *  this function assumed a tag's content was always a single word's
 *  letters and never contained a word-separating space; that assumption is
 *  false, and silently dropped ALL tajweed coloring for any verse
 *  containing such a tag (measured at ~72% of verses in a cross-check
 *  across pages 1-49 — not a rare corner case).
 *
 *  Fixed by determining word boundaries from the FLATTENED (tags-stripped)
 *  text's literal space characters — exactly how `v.words` is itself
 *  space-tokenized upstream — rather than from the tag structure. Colors
 *  are tracked per character in parallel, then re-grouped into contiguous
 *  same-color sub-runs within each word (see groupRun), so a tag that
 *  crosses a word boundary now correctly splits into two colored
 *  word-chunks instead of either breaking the count or merging the words
 *  together. A leading Rub el-Hizb/Sajdah marker is merged forward into
 *  the next real word rather than counted as one of its own (see
 *  MARK_ONLY_RE above). Returns an array of chunks, each chunk itself an
 *  array of `{text, color}` runs (a single word can contain more than one
 *  colored letter run).
 *
 *  KNOWN REMAINING GAP: quran.com's markup can nest a <tajweed> tag inside
 *  another <tajweed> tag of the same name (real example: 2:195's
 *  `<tajweed class=madda_obligatory>ُوٓ<tajweed class=slnt>اْ</tajweed>ۛ</tajweed>`).
 *  parseTajweedRuns's single-pass regex can't balance same-name nesting, so
 *  a verse with this pattern degrades gracefully to plain/uncolored (the
 *  chunk-count mismatch is caught by MadaniPage's own guard, which skips
 *  coloring for just that verse) rather than corrupting anything — measured
 *  at 1 verse in a 46-verse sample. Not fixed here; would need a proper
 *  nested/stack-based tag parser to close, which is a larger change for a
 *  rare, non-corrupting case. */
export function splitTajweedByWord(raw) {
  const runs = parseAllRuns(raw);

  // Flatten to one string plus a parallel per-character color array, so
  // word boundaries can be found independently of which run(s) contributed
  // which characters.
  let fullText = "";
  const colorAt = [];
  runs.forEach((run) => {
    for (const ch of run.text) {
      fullText += ch;
      colorAt.push(run.color);
    }
  });

  const words = [];
  let pendingPrefix = []; // a marker-only chunk, waiting to attach to the next real word
  let wordStart = 0;
  for (let i = 0; i <= fullText.length; i++) {
    const atBoundary = i === fullText.length || fullText[i] === " ";
    if (!atBoundary) continue;
    if (i > wordStart) {
      const chunk = groupRun(fullText, colorAt, wordStart, i);
      const plain = chunk.map((r) => r.text).join("");
      if (MARK_ONLY_RE.test(plain)) {
        // Not a word of its own — carry it (plus the space that followed
        // it) forward onto whatever the next real word turns out to be.
        pendingPrefix = pendingPrefix.concat(chunk, [{ text: " ", color: null }]);
      } else {
        words.push(pendingPrefix.length ? pendingPrefix.concat(chunk) : chunk);
        pendingPrefix = [];
      }
    }
    wordStart = i + 1;
  }
  // Shouldn't happen in practice (a verse can't end in a bare marker), but
  // don't silently drop it if it ever does.
  if (pendingPrefix.length) words.push(pendingPrefix);
  return words;
}

const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";
export function toArabicDigits(n) {
  return String(n).replace(/[0-9]/g, (d) => ARABIC_DIGITS[+d]);
}
